if (!customElements.get("media-gallery")) {
  customElements.define(
    "media-gallery",
    class MediaGallery extends HTMLElement {
      constructor() {
        super();

        this.selectors = {
          container: ".m-main-product--wrapper",
          slider: ".swiper-container",
          sliderPagination: ".swiper-pagination",
          sliderPrevEl: ".swiper-button-prev",
          sliderNextEl: ".swiper-button-next",
          navSlider: ".nav-swiper-container",
          medias: [".m-product-media--item"],
          mediaWrapper: ".m-product-media--wrapper",
          mediaZoomIns: [".m-product-media__zoom-in"],
          videos: [".media-video"],
          variantIdNode: '[name="id"]',
          featuredImage: ".m-product-media",
        };

        this.productSlideCommonConfigs = {
          loop: true,
        };

        this.productSlideConfigs = {
          mobile: {
            autoHeight: true,
            loop: true,
            allowTouchMove: true,
            simulateTouch: true,
          },

          "quick-view": {
            autoHeight: true,
          },

          "layout-5": {
            ...this.productSlideCommonConfigs,
            slidesPerView: 1,
            slidesPerGroup: 1,
            watchSlidesProgress: true,
            watchSlidesVisibility: true,
            allowTouchMove: true,
            simulateTouch: true,

            breakpoints: {
              768: {
                slidesPerView: 2,
                slidesPerGroup: 1,
                spaceBetween: 10,
              },
            },
          },

          /*
           * FIXED LAYOUT 6
           * Main product image slider
           */
          "layout-6": {
            ...this.productSlideCommonConfigs,
            slidesPerView: 1,
            slidesPerGroup: 1,
            spaceBetween: 0,
            autoHeight: true,
            watchSlidesProgress: true,
            watchSlidesVisibility: true,

            allowTouchMove: true,
            simulateTouch: true,
            touchRatio: 1,
            touchAngle: 45,

            observer: true,
            observeParents: true,
            observeSlideChildren: true,
          },

          "layout-7": {
            ...this.productSlideCommonConfigs,
            slidesPerView: 3,
            speed: 500,
            centeredSlides: true,
            allowTouchMove: true,
            simulateTouch: true,
          },
        };

        this.container = this.closest(
          this.selectors.container
        );

        this.domNodes = queryDomNodes(
          this.selectors,
          this
        );

        this.enableVideoAutoplay =
          this.dataset.enableVideoAutoplay === "true";

        this.enableImageZoom =
          this.dataset.enableImageZoom === "true";

        this.enableVariantGroupImages =
          this.dataset.enableVariantGroupImages === "true";

        this.show_featured_media =
          this.dataset.showFeaturedMedia === "true";

        this.onlyMedia =
          this.dataset.onlyMedia === "true";

        if (this.container) {
          this.view =
            this.container.dataset.view ||
            "product-template";
        }

        this.section = this.closest(
          `[data-section-id="${this.sectionId}"]`
        );

        this.layout = this.dataset.layout;

        this.fetchProductData();
      }

      getProductSliderConfig(layout) {
        const conf =
          this.productSlideConfigs[layout] ||
          this.productSlideConfigs["layout-5"];

        return {
          ...this.productSlideCommonConfigs,
          ...conf,
        };
      }

      async fetchProductData() {
        try {
          const product =
            await this.getProductJson();

          if (product) {
            this.productData = product;
            this.setupData();
          }
        } catch (error) {
          console.error(
            "Error fetching product:",
            error
          );
        }
      }

      setupData() {
        this.productHandle =
          this.dataset.productHandle;

        this.productUrl =
          this.dataset.productUrl;

        const { variants } =
          this.productData || {};

        if (
          !this.productData ||
          !variants
        ) {
          return;
        }

        const variantIdNode =
          this.container?.querySelector(
            this.selectors.variantIdNode
          );

        let currentVariantId =
          Number(
            variantIdNode?.value
          ) ||
          this.productData
            .selected_or_first_available_variant
            ?.id;

        const currentVariant =
          variants.find(
            (variant) =>
              variant.id ===
              currentVariantId
          ) || variants[0];

        this.productData.initialVariant =
          currentVariant;

        if (
          !this.productData.selected_variant &&
          variantIdNode?.dataset.selectedVariant
        ) {
          const selectedVariantId =
            Number(
              variantIdNode.dataset
                .selectedVariant
            );

          this.productData.selected_variant =
            variants.find(
              (variant) =>
                variant.id ===
                selectedVariantId
            );
        }

        this.init();
      }

      init() {
        const variantPicker =
          this.container?.querySelector(
            "variant-picker"
          );

        const commonInit = () => {
          this.handleAutoplayVideo();

          if (!variantPicker) {
            this.initPhotoswipe();
            this.handleSlideChange();

            this.removeAttribute(
              "data-media-loading"
            );

            if (this.firstElementChild) {
              this.firstElementChild.style.opacity =
                1;
            }
          }
        };

        switch (this.view) {
          case "product-template":
            this.mediaMode = "slider";

            this.handlePhotoswipe();
            this.initPhotoswipe();
            this.initSlider();

            break;

          case "featured-product":
            this.mediaMode = "slider";

            this.initSlider();

            break;

          case "card":
          case "sticky-atc":
          case "quick-view":
            this.mediaMode =
              "featured-image";

            if (
              this.view ===
              "quick-view"
            ) {
              this.initSlider();
            }

            break;

          default:
            console.warn(
              "Unknown product view:",
              this,
              this.view
            );
        }

        commonInit();
      }

      /*
       * =========================================
       * MAIN SLIDER INITIALIZATION
       * =========================================
       */
      initSlider() {
        /*
         * Get ONLY the main slider.
         *
         * Important:
         * Layout 6 also contains a nested
         * thumbnail swiper, so we must not
         * accidentally initialize the thumbnail
         * as the main slider.
         */
        const slider =
          this.querySelector(
            ".m-product-media--slider__images > .swiper-container"
          ) ||
          this.querySelector(
            ".m-product-media--slider__images .swiper-container"
          );

        if (!slider) {
          return;
        }

        const slides =
          slider.querySelectorAll(
            ":scope > .swiper-wrapper > .swiper-slide"
          );

        if (!slides.length) {
          return;
        }

        this.mediaMode = "slider";

        /*
         * Get arrows from THIS product gallery.
         */
        const nextEl =
          this.querySelector(
            ".m-product-media--slider__navigation .swiper-button-next"
          ) ||
          this.querySelector(
            ".swiper-button-next"
          );

        const prevEl =
          this.querySelector(
            ".m-product-media--slider__navigation .swiper-button-prev"
          ) ||
          this.querySelector(
            ".swiper-button-prev"
          );

        const sliderPagination =
          this.querySelector(
            ".m-product-media--slider__pagination"
          ) ||
          this.querySelector(
            ".swiper-pagination"
          );

        const navSlider =
          this.querySelector(
            ".nav-swiper-container"
          );

        /*
         * ALWAYS START WITH FIRST IMAGE.
         */
        const initialSlide = 0;

        /*
         * =========================================
         * THUMBNAIL SLIDER
         * =========================================
         */
        this.navSlider = null;

        if (navSlider) {
          const configNav =
            this.getNavSliderConfig(
              initialSlide
            );

          this.navSlider =
            new MinimogLibs.Swiper(
              navSlider,
              configNav
            );
        }

        /*
         * =========================================
         * MAIN SLIDER CONFIG
         * =========================================
         *
         * Navigation is intentionally disabled
         * here because we manually connect the
         * actual visible arrows below.
         */
        const config =
          this.getMainSliderConfig(
            initialSlide,
            {
              nextEl: null,
              prevEl: null,
              sliderPagination,
            }
          );

        /*
         * Destroy any old Swiper instance.
         */
        if (slider.swiper) {
          try {
            slider.swiper.destroy(
              true,
              true
            );
          } catch (error) {
            console.warn(
              "Could not destroy previous Swiper instance:",
              error
            );
          }
        }

        /*
         * Initialize main slider.
         */
        this.slider =
          new MinimogLibs.Swiper(
            slider,
            config
          );

        /*
         * =========================================
         * MANUAL ARROW CONNECTION
         * =========================================
         *
         * This is the important fix.
         */
        if (prevEl) {
          prevEl.onclick = (event) => {
            event.preventDefault();
            event.stopPropagation();

            if (
              this.slider &&
              typeof this.slider.slidePrev ===
                "function"
            ) {
              this.slider.slidePrev();
            }
          };

          prevEl.addEventListener(
            "touchend",
            (event) => {
              event.preventDefault();

              if (
                this.slider &&
                typeof this.slider.slidePrev ===
                  "function"
              ) {
                this.slider.slidePrev();
              }
            },
            {
              passive: false,
            }
          );
        }

        if (nextEl) {
          nextEl.onclick = (event) => {
            event.preventDefault();
            event.stopPropagation();

            if (
              this.slider &&
              typeof this.slider.slideNext ===
                "function"
            ) {
              this.slider.slideNext();
            }
          };

          nextEl.addEventListener(
            "touchend",
            (event) => {
              event.preventDefault();

              if (
                this.slider &&
                typeof this.slider.slideNext ===
                  "function"
              ) {
                this.slider.slideNext();
              }
            },
            {
              passive: false,
            }
          );
        }

        /*
         * =========================================
         * FORCE FIRST IMAGE
         * =========================================
         */
        const showFirstImage = () => {
          if (!this.slider) {
            return;
          }

          try {
            if (
              this.enableVariantGroupImages
            ) {
              this.slider.slideTo(
                0,
                0,
                false
              );
            } else {
              this.slider.slideToLoop(
                0,
                0,
                false
              );
            }

            this.slider.update();

            if (this.navSlider) {
              this.navSlider.update();

              this.navSlider.slideTo(
                0,
                0,
                false
              );
            }
          } catch (error) {
            console.warn(
              "Could not set first product image:",
              error
            );
          }
        };

        /*
         * Wait until Swiper has completed
         * initialization.
         */
        if (
          this.slider.initialized
        ) {
          requestAnimationFrame(
            () => {
              showFirstImage();
            }
          );
        } else {
          setTimeout(
            () => {
              showFirstImage();
            },
            100
          );
        }

        /*
         * Update slider after images have loaded.
         */
        setTimeout(() => {
          if (this.slider) {
            try {
              this.slider.update();
              this.slider.updateSize();
              this.slider.updateSlides();
            } catch (error) {
              console.warn(
                "Slider update error:",
                error
              );
            }
          }

          if (this.navSlider) {
            try {
              this.navSlider.update();
            } catch (error) {
              console.warn(
                "Thumbnail slider update error:",
                error
              );
            }
          }
        }, 300);

        if (
          !this.enableVariantGroupImages
        ) {
          this.handleSlideChange();
        }
      }

      /*
       * =========================================
       * THUMBNAIL SLIDER
       * =========================================
       */
      getNavSliderConfig(
        initialSlide
      ) {
        return {
          loop: false,

          initialSlide:
            initialSlide || 0,

          slidesPerView: 5,

          freeMode: true,

          spaceBetween:
            this.layout === "layout-6"
              ? 5
              : 10,

          threshold: 2,

          watchSlidesVisibility:
            true,

          watchSlidesProgress:
            true,

          allowTouchMove: true,

          simulateTouch: true,

          observer: true,

          observeParents: true,

          breakpoints: {
            1024: {
              direction:
                this.layout ===
                "layout-6"
                  ? "vertical"
                  : "horizontal",
            },
          },

          on: {
            init: () => {
              if (
                this.domNodes
                  .navSlider
              ) {
                this.domNodes.navSlider.style.opacity =
                  1;
              }
            },
          },
        };
      }

      /*
       * =========================================
       * MAIN SLIDER CONFIG
       * =========================================
       */
      getMainSliderConfig(
        initialSlide,
        {
          nextEl,
          prevEl,
          sliderPagination,
        }
      ) {
        const thumbsConfig =
          this.navSlider
            ? {
                thumbs: {
                  swiper:
                    this.navSlider,
                },
              }
            : {};

        return {
          ...this.getProductSliderConfig(
            this.layout
          ),

          initialSlide:
            initialSlide || 0,

          autoHeight: true,

          /*
           * We manually control arrows.
           */
          navigation: false,

          /*
           * Mouse drag + touch swipe.
           */
          allowTouchMove: true,

          simulateTouch: true,

          touchRatio: 1,

          touchAngle: 45,

          threshold: 2,

          /*
           * Keep loop.
           */
          loop:
            !this
              .enableVariantGroupImages,

          /*
           * Better refresh when product
           * page changes.
           */
          observer: true,

          observeParents: true,

          observeSlideChildren: true,

          /*
           * Pagination.
           */
          pagination: {
            el:
              sliderPagination ||
              null,

            clickable: true,

            type: "bullets",
          },

          /*
           * Thumbnail connection.
           */
          ...thumbsConfig,

          on: {
            init: (swiper) => {
              if (
                this.domNodes
                  .slider
              ) {
                this.domNodes.slider.style.opacity =
                  1;
              }

              this.domNodes =
                queryDomNodes(
                  this.selectors,
                  this.container ||
                    this
                );

              requestAnimationFrame(
                () => {
                  window.dispatchEvent(
                    new Event(
                      "resize"
                    )
                  );
                }
              );

              /*
               * Make sure first thumbnail
               * is selected initially.
               */
              this.updateActiveThumbnail(
                swiper
              );
            },

            slideChange: (
              swiper
            ) => {
              this.updateActiveThumbnail(
                swiper
              );
            },

            resize: (
              swiper
            ) => {
              try {
                swiper.update();
              } catch (error) {}
            },
          },
        };
      }

      /*
       * =========================================
       * THUMBNAIL SYNC
       * =========================================
       */
      updateActiveThumbnail(
        swiper
      ) {
        if (
          !this.navSlider ||
          !swiper
        ) {
          return;
        }

        let realIndex =
          typeof swiper.realIndex ===
          "number"
            ? swiper.realIndex
            : swiper.activeIndex || 0;

        if (realIndex < 0) {
          realIndex = 0;
        }

        try {
          this.navSlider.slideTo(
            realIndex,
            300,
            false
          );
        } catch (error) {}
      }

      /*
       * =========================================
       * SLIDE CHANGE / VIDEO / DRAG
       * =========================================
       */
      handleSlideChange() {
        if (!this.slider) {
          return;
        }

        /*
         * Prevent duplicate listener.
         */
        if (
          this._slideChangeHandler
        ) {
          this.slider.off(
            "slideChange",
            this._slideChangeHandler
          );
        }

        this._slideChangeHandler = (
          swiper
        ) => {
          window.pauseAllMedia(
            this
          );

          const {
            slides,
            activeIndex,
          } = swiper;

          const activeSlide =
            slides[
              activeIndex
            ];

          if (activeSlide) {
            this.playActiveMedia(
              activeSlide
            );
          }

          const realIndex =
            typeof swiper.realIndex ===
            "number"
              ? swiper.realIndex
              : activeIndex;

          const visibleSlides = [
            realIndex,
          ];

          if (
            [
              "layout-5",
              "layout-7",
            ].includes(
              this.layout
            )
          ) {
            visibleSlides.push(
              realIndex + 1
            );
          }

          const hasModelMediaType =
            visibleSlides.some(
              (index) => {
                const slide =
                  slides[index];

                return (
                  slide &&
                  slide.dataset
                    .mediaType ===
                    "model"
                );
              }
            );

          this.toggleSliderDraggableState(
            !hasModelMediaType
          );

          this.updateActiveThumbnail(
            swiper
          );
        };

        this.slider.on(
          "slideChange",
          this._slideChangeHandler
        );
      }

      toggleSliderDraggableState(
        isDraggable
      ) {
        if (!this.slider) {
          return;
        }

        this.slider.allowTouchMove =
          isDraggable;
      }

      /*
       * =========================================
       * VIDEO AUTOPLAY
       * =========================================
       */
      handleAutoplayVideo() {
        const playVideo = (
          mediaElement
        ) => {
          if (!mediaElement) {
            return;
          }

          const mediaType =
            mediaElement.dataset
              .mediaType;

          if (
            mediaType ===
              "video" ||
            mediaType ===
              "external_video"
          ) {
            const deferredMedia =
              mediaElement.querySelector(
                "deferred-media"
              );

            const autoplay =
              deferredMedia &&
              deferredMedia.dataset
                .autoPlay ===
                "true";

            if (autoplay) {
              deferredMedia.loadContent(
                false
              );
            }
          }
        };

        if (
          this.mediaMode ===
            "slider" &&
          this.slider
        ) {
          const {
            slides,
            activeIndex,
          } = this.slider;

          const activeSlide =
            slides[
              activeIndex
            ];

          if (activeSlide) {
            playVideo(
              activeSlide
            );
          }
        } else {
          const allMedia =
            this.querySelectorAll(
              ".m-product-media--item"
            );

          allMedia.forEach(
            playVideo
          );
        }
      }

      playActiveMedia(
        selected
      ) {
        if (!selected) {
          return;
        }

        const deferredMedia =
          selected.querySelector(
            "deferred-media"
          );

        if (
          !deferredMedia ||
          deferredMedia.dataset
            .autoPlay !==
            "true"
        ) {
          return;
        }

        const playMedia = (
          element
        ) => {
          if (!element) {
            return;
          }

          if (
            element.classList.contains(
              "js-youtube"
            ) ||
            element.classList.contains(
              "js-vimeo"
            )
          ) {
            const platform =
              element.classList.contains(
                "js-youtube"
              )
                ? "youtube"
                : "vimeo";

            const param =
              platform ===
              "youtube"
                ? "mute=1"
                : "muted=1";

            const symbol =
              element.src.includes(
                "?"
              )
                ? "&"
                : "?";

            element.src +=
              `${symbol}autoplay=1&${param}`;
          } else if (
            typeof element.play ===
            "function"
          ) {
            element
              .play()
              .catch(
                () => {}
              );
          }
        };

        if (
          !deferredMedia.hasAttribute(
            "loaded"
          )
        ) {
          deferredMedia.loadContent(
            false
          );
        }

        const deferredElement =
          deferredMedia.querySelector(
            "video, model-viewer, iframe"
          );

        if (
          deferredElement
        ) {
          playMedia(
            deferredElement
          );
        }
      }

      /*
       * =========================================
       * PHOTOSWIPE
       * =========================================
       */
      handlePhotoswipe(
        customData = []
      ) {
        if (
          !this.enableImageZoom
        ) {
          return;
        }

        const medias = [
          ...this.querySelectorAll(
            ".m-product-media--item:not(.swiper-slide-duplicate)"
          ),
        ];

        const data =
          medias
            .map(
              (media) => {
                const {
                  mediaType,
                  index: id,
                } = media.dataset;

                if (
                  mediaType ===
                  "image"
                ) {
                  const mediaElement =
                    media.querySelector(
                      ".m-product-media"
                    );

                  if (
                    !mediaElement
                  ) {
                    return null;
                  }

                  const {
                    mediaSrc:
                      src,
                    mediaWidth,
                    mediaHeight,
                    mediaAlt:
                      alt,
                  } =
                    mediaElement.dataset;

                  return {
                    src,
                    width:
                      parseInt(
                        mediaWidth
                      ) || 1,
                    height:
                      parseInt(
                        mediaHeight
                      ) || 1,
                    alt,
                    id,
                  };
                }

                return {
                  html: `<div class="pswp__${mediaType}">${media.innerHTML}</div>`,
                  type:
                    mediaType,
                  id,
                };
              }
            )
            .filter(
              Boolean
            );

        const options = {
          dataSource:
            customData.length
              ? customData
              : data,

          bgOpacity: 1,

          close: false,

          zoom: false,

          arrowNext: false,

          arrowPrev: false,

          counter: false,

          preloader: false,

          pswpModule:
            MinimogLibs
              .PhotoSwipeLightbox
              .PhotoSwipe,
        };

        this.lightbox =
          new MinimogLibs
            .PhotoSwipeLightbox(
              options
            );

        this.lightbox.addFilter(
          "thumbEl",
          (
            thumbEl,
            { id }
          ) => {
            return (
              this.querySelector(
                `[data-index="${id}"]:not(.swiper-slide-duplicate) img`
              ) ||
              thumbEl
            );
          }
        );

        this.lightbox.addFilter(
          "placeholderSrc",
          (
            placeholderSrc,
            {
              data: { id },
            }
          ) => {
            const el =
              this.querySelector(
                `[data-index="${id}"]:not(.swiper-slide-duplicate) img`
              );

            return el
              ? el.src
              : placeholderSrc;
          }
        );

        this.lightbox.on(
          "change",
          () => {
            window.pauseAllMedia(
              this
            );

            if (
              this.slider
            ) {
              const {
                currIndex,
              } =
                this.lightbox
                  .pswp;

              const slideMethod =
                this
                  .enableVariantGroupImages
                  ? "slideTo"
                  : "slideToLoop";

              this.slider[
                slideMethod
              ](
                currIndex,
                100,
                false
              );
            }
          }
        );

        this.lightbox.on(
          "pointerDown",
          (e) => {
            if (
              this.lightbox
                .pswp
                .currSlide
                .data
                .type ===
              "model"
            ) {
              e.preventDefault();
            }
          }
        );

        const uiElements =
          [
            {
              name:
                "m-close",

              order: 11,

              isButton:
                true,

              html: '<svg class="m-svg-icon--large" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path></svg>',

              onClick: (
                event,
                el,
                pswp
              ) =>
                pswp.close(),
            },

            {
              name:
                "m-arrowNext",

              order: 12,

              isButton:
                true,

              html: '<svg fill="currentColor" width="14px" height="14px" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512"><path d="M218.101 38.101L198.302 57.9c-4.686 4.686-4.686 12.284 0 16.971L353.432 230H12c-6.627 0-12 5.373-12 12v28c0 6.627 5.373 12 12 12h341.432l-155.13 155.13c-4.686 4.686-12.284 4.686-16.971 0l-19.799-19.799c-4.686-4.686-4.686-12.284 0-16.971l209.414-209.414c4.686-4.686 4.686-12.284 0-16.971L235.071 38.101c-4.687-4.687-12.284-4.687-16.97 0z"></path></svg>',

              onClick: (
                event,
                el,
                pswp
              ) =>
                pswp.next(),
            },

            {
              name:
                "m-arrowPrev",

              order: 10,

              isButton:
                true,

              html: '<svg width="14px" height="14px" fill="currentColor" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512"><path d="M229.9 473.899l19.799-19.799c4.686-4.686 4.686-12.284 0-16.971L94.569 282H436c6.627 0 12-5.373 12-12v-28c0-6.627-5.373-12-12-12H94.569l155.13-155.13c4.686-4.686 4.686-12.284 0-16.971L229.9 38.101c-4.686-4.686-12.284-4.686-16.971 0L3.515 247.515c-4.686 4.686-4.686 12.284 0 16.971L212.929 473.9c4.686 4.686 12.284 4.686 16.971-.001z"></path></svg>',

              onClick: (
                event,
                el,
                pswp
              ) =>
                pswp.prev(),
            },
          ];

        this.lightbox.on(
          "uiRegister",
          () => {
            this.lightbox.pswp.ui.registerElement(
              uiElements[0]
            );

            if (
              !this.onlyMedia
            ) {
              uiElements
                .slice(1)
                .forEach(
                  (
                    element
                  ) =>
                    this.lightbox.pswp.ui.registerElement(
                      element
                    )
                );
            }
          }
        );
      }

      initPhotoswipe() {
        if (
          !this.enableImageZoom
        ) {
          return;
        }

        if (
          !this.lightbox
        ) {
          return;
        }

        this.lightbox.init();

        const handleMediaClick = (
          e,
          media
        ) => {
          e.preventDefault();

          const isImage =
            media.classList.contains(
              "media-type-image"
            );

          const isZoomButton =
            e.target.closest(
              this.selectors
                .mediaZoomIns[0]
            );

          if (
            isImage ||
            isZoomButton
          ) {
            const index =
              Number(
                media.dataset
                  .index
              ) || 0;

            this.lightbox.loadAndOpen(
              index
            );
          }
        };

        addEventDelegate({
          selector:
            this.selectors
              .medias[0],

          context:
            this,

          handler:
            handleMediaClick,
        });
      }

      /*
       * =========================================
       * VARIANT MEDIA
       * =========================================
       */
      setActiveMedia(
        variant
      ) {
        if (!variant) {
          return;
        }

        switch (
          this.mediaMode
        ) {
          case "slider":
            this.setActiveMediaForSlider(
              variant
            );
            break;

          case "featured-image":
            this.setActiveMediaForFeaturedImage(
              variant
            );
            break;

          default:
            this.handleGalleryMode(
              variant
            );
        }
      }

      setActiveMediaForSlider(
        variant
      ) {
        if (
          variant.featured_media &&
          this.slider &&
          this.slider.wrapperEl
        ) {
          const slideIndex =
            Number(
              variant.featured_media
                .position
            ) - 1;

          if (
            slideIndex >= 0
          ) {
            this.slider.slideToLoop(
              slideIndex,
              300
            );
          }
        }
      }

      setActiveMediaForFeaturedImage(
        variant
      ) {
        if (
          variant.featured_image
        ) {
          const src =
            variant
              .featured_image
              .src;

          const {
            featuredImage,
          } = this.domNodes;

          if (
            !featuredImage
          ) {
            return;
          }

          const img =
            featuredImage.querySelector(
              "img"
            );

          if (
            img &&
            src
          ) {
            img.src = src;
          }
        }
      }

      handleGalleryMode(
        variant
      ) {
        if (
          this.view !==
            "featured-product" &&
          variant &&
          variant.featured_media
        ) {
          const selectedMedia =
            this.querySelector(
              `[data-media-id="${variant.featured_media.id}"]`
            );

          if (
            selectedMedia
          ) {
            this.scrollIntoView(
              selectedMedia
            );
          }
        }
      }

      scrollIntoView(
        selectedMedia
      ) {
        selectedMedia.scrollIntoView(
          {
            behavior:
              "smooth",

            block:
              "nearest",
          }
        );
      }

      /*
       * =========================================
       * PRODUCT JSON
       * =========================================
       */
      getProductJson() {
        return fetch(
          window.Shopify.routes.root +
            `products/${this.dataset.productHandle}.js`
        )
          .then(
            (response) => {
              if (
                !response.ok
              ) {
                throw new Error(
                  "Network response was not ok"
                );
              }

              return response.json();
            }
          )
          .then(
            (product) =>
              product
          )
          .catch(
            (error) => {
              console.error(
                "There has been a problem with your fetch operation:",
                error
              );
            }
          );
      }
    }
  );
}